import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import tokenStore from '../utils/tokenStore';
import { API_URL } from '../constants/api';
import { useToast } from './ToastContext';
import { appendFocusLog } from '../utils/focusLog';

const ScheduleContext = createContext();

export const useSchedule = () => {
  const context = useContext(ScheduleContext);
  if (!context) {
    throw new Error('useSchedule must be used within a ScheduleProvider');
  }
  return context;
};

/* per-type visuals for the completion celebration card */
const TYPE_CONFIG = {
  'deep-work': { emoji: '🟣', dot: '#a855f7' },
  'meetings':  { emoji: '💬', dot: '#10b981' },
  'admin':     { emoji: '📋', dot: '#f59e0b' },
  'learning':  { emoji: '📚', dot: '#6366f1' },
  'creation':  { emoji: '✨', dot: '#ec4899' },
};
const typeConf = (t) => TYPE_CONFIG[t] || { emoji: '🔲', dot: '#6b7280' };

/* "HH:MM" (today) → Date with seconds zeroed */
const timeToDate = (t) => {
  if (!t) return null;
  const [h, m] = t.split(':').map(Number);
  const d = new Date();
  d.setHours(h, m || 0, 0, 0);
  return d;
};

/* local YYYY-MM-DD — avoids UTC-offset mismatches */
const localDate = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export const ScheduleProvider = ({ children }) => {
  const { showToast } = useToast();

  const [timeBlocks, setTimeBlocks] = useState([]);

  /*
   * liveBlock — the currently running schedule block, fully self-contained.
   * Shape: { id, title, type, area, start_time, end_time, secLeft, totalSec, startedAt, isPaused }
   * `startedAt` (ms epoch) + `totalSec` are the source of truth; `secLeft` is
   * derived from the wall clock so background-tab throttling can't cause drift.
   */
  const [liveBlock, setLiveBlock] = useState(() => {
    try {
      const saved = localStorage.getItem('liveTimeBlock');
      if (!saved) return null;
      const parsed = JSON.parse(saved);
      if (!parsed) return null;
      if (parsed.isPaused || !parsed.startedAt) return parsed;
      const elapsed = Math.floor((Date.now() - parsed.startedAt) / 1000);
      const secLeft = Math.max(0, parsed.totalSec - elapsed);
      return secLeft > 0 ? { ...parsed, secLeft } : null;
    } catch (e) { return null; }
  });

  /* completion celebration — { title, type, durationMin } */
  const [completedBlock, setCompletedBlock] = useState(null);
  const toastTimerRef = useRef(null);

  const liveBlockRef = useRef(liveBlock);
  liveBlockRef.current = liveBlock;
  const timeBlocksRef = useRef(timeBlocks);
  timeBlocksRef.current = timeBlocks;

  /* id of the block currently tracked as "live" (auto or manual) — prevents
   * immediately re-auto-starting the same block right after it ends/is dismissed */
  const autoStartedRef = useRef(liveBlock ? liveBlock.id : null);
  /* ids reminded-about today, so the 30s reminder fires only once per block */
  const remindedRef = useRef(new Set());

  /* persist liveBlock across reloads */
  useEffect(() => {
    try {
      if (liveBlock) localStorage.setItem('liveTimeBlock', JSON.stringify(liveBlock));
      else localStorage.removeItem('liveTimeBlock');
    } catch (e) { /* ignore */ }
  }, [liveBlock]);

  /* fetch today's time blocks — independent of any page being mounted, so
   * auto-start / reminders / countdown keep working no matter which tab the
   * user is on */
  const fetchTimeBlocks = useCallback(async () => {
    try {
      const token = tokenStore.get();
      if (!token) return;
      const r = await axios.get(`${API_URL}/api/time_blocks`, { headers: { Authorization: `Bearer ${token}` } });
      if (Array.isArray(r.data)) {
        const today = localDate();
        setTimeBlocks(r.data.filter(b => !b.date || b.date === today));
      }
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    fetchTimeBlocks();
    window.addEventListener('fp:timeBlockAdded', fetchTimeBlocks);
    return () => window.removeEventListener('fp:timeBlockAdded', fetchTimeBlocks);
  }, [fetchTimeBlocks]);

  /* auto-dismiss completion celebration after 5s */
  useEffect(() => {
    if (!completedBlock) return;
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setCompletedBlock(null), 5000);
    return () => clearTimeout(toastTimerRef.current);
  }, [completedBlock]);

  /* Record a finished block in the focus log — locally first (so it shows up in
   * "Today's Schedule → Focus log" immediately) then best-effort to the backend,
   * matching what TimerContext does for Pomodoro sessions. Blocks shorter than a
   * minute are dropped so an accidental start/stop doesn't clutter the log. */
  const logBlockSession = useCallback((block, focusedSec, endedEarly) => {
    const minutes = Math.round(focusedSec / 60);
    if (!block || minutes < 1) return;

    appendFocusLog({
      kind: 'block',
      title: block.title,
      type: block.type,
      area: block.area,
      minutes,
      plannedMin: Math.round(block.totalSec / 60),
      endedEarly: !!endedEarly,
    });

    try {
      const token = tokenStore.get();
      if (!token) return;
      axios.post(`${API_URL}/api/focus-sessions`, {
        duration: minutes,
        task_name: block.title || 'Time block',
        session_type: 'time_block',
        time_block_id: block.id || null,
        completed_at: new Date().toISOString(),
      }, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } })
        .catch(() => { /* local mirror already has it */ });
    } catch (e) { /* ignore */ }
  }, []);

  /* Recompute the live block's countdown from the wall clock (startedAt → now)
   * instead of decrementing by 1 per tick, so background-tab throttling can't
   * make it drift or freeze. Called by the master interval AND immediately on
   * tab refocus. */
  const recalcLiveBlock = useCallback(() => {
    const lb = liveBlockRef.current;
    if (!lb || lb.isPaused || !lb.startedAt) return;

    const elapsed = Math.floor((Date.now() - lb.startedAt) / 1000);
    const newSecLeft = Math.max(0, lb.totalSec - elapsed);
    if (newSecLeft === lb.secLeft) return;

    if (newSecLeft <= 0) {
      const durationMin = Math.round(lb.totalSec / 60);
      logBlockSession(lb, lb.totalSec, false);
      setCompletedBlock({ title: lb.title, type: lb.type, durationMin });
      setLiveBlock(null);
      return;
    }
    setLiveBlock(prev => prev ? { ...prev, secLeft: newSecLeft } : null);
  }, [logBlockSession]);

  /* ── Master interval: countdown + 30s-before reminder + auto-start ──────
   * Empty deps so the closure never goes stale — mutable values are read
   * through refs that are refreshed on every render. Runs at the app root,
   * so it keeps ticking no matter which page/tab the user is viewing. */
  useEffect(() => {
    const id = setInterval(() => {
      const lb = liveBlockRef.current;

      if (lb) {
        recalcLiveBlock();
        return; /* a block is tracked (running or paused) — skip reminder/auto-start */
      }

      const now2   = new Date();
      const blocks = timeBlocksRef.current;
      const sorted = [...blocks].sort((a, b) => (timeToDate(a.start_time) || 0) - (timeToDate(b.start_time) || 0));

      /* ── 30-second-before reminder ──────────────────────────────────── */
      const upcoming = sorted.find(b => {
        const startD = timeToDate(b.start_time);
        if (!startD) return false;
        const diffMs = startD.getTime() - now2.getTime();
        return diffMs > 0 && diffMs <= 30000;
      });
      if (upcoming && !remindedRef.current.has(String(upcoming.id))) {
        remindedRef.current.add(String(upcoming.id));
        showToast(`⏰ "${upcoming.title}" starts in 30s — tap Start to begin now`, 'info', { duration: 8000 });
      }

      /* ── auto-start ──────────────────────────────────────────────────── */
      const hit = sorted.find(b => {
        const startD = timeToDate(b.start_time);
        const endD   = timeToDate(b.end_time);
        if (!startD || !endD) return false;
        return startD <= now2 && now2 < endD;
      });

      if (hit && autoStartedRef.current !== String(hit.id)) {
        const endD   = timeToDate(hit.end_time);
        const remSec = Math.max(30, Math.round((endD.getTime() - now2.getTime()) / 1000));
        autoStartedRef.current = String(hit.id);
        setLiveBlock({
          id: String(hit.id),
          title:      hit.title,
          type:       hit.type  || 'deep-work',
          area:       hit.area  || 'build',
          start_time: hit.start_time,
          end_time:   hit.end_time,
          secLeft:    remSec,
          totalSec:   remSec,
          startedAt:  Date.now(),
          isPaused:   false,
        });
      } else if (!hit) {
        autoStartedRef.current = null;
      }
    }, 1000);
    return () => clearInterval(id);
  }, [recalcLiveBlock, showToast]);

  /* Resync instantly on tab refocus — background tabs get setInterval throttled
   * or fully suspended, so on return we recompute from the wall clock right away
   * instead of waiting for the next tick. */
  useEffect(() => {
    const resync = () => {
      if (document.visibilityState === 'visible') recalcLiveBlock();
    };
    document.addEventListener('visibilitychange', resync);
    window.addEventListener('focus', resync);
    return () => {
      document.removeEventListener('visibilitychange', resync);
      window.removeEventListener('focus', resync);
    };
  }, [recalcLiveBlock]);

  /* Manual start — "focus on the user input": always counts down the block's
   * full configured duration (end_time − start_time), regardless of what the
   * clock currently reads (i.e. not clipped to the remaining scheduled window). */
  const startBlock = useCallback((block, secOverride) => {
    const startD = timeToDate(block.start_time);
    const endD   = timeToDate(block.end_time);
    const fullSec = (startD && endD) ? Math.round((endD.getTime() - startD.getTime()) / 1000) : 1500;
    const totalSec = Math.max(30, secOverride != null ? secOverride : fullSec);
    autoStartedRef.current = String(block.id);
    setLiveBlock({
      id: String(block.id),
      title:      block.title,
      type:       block.type  || 'deep-work',
      area:       block.area  || 'build',
      start_time: block.start_time,
      end_time:   block.end_time,
      secLeft:    totalSec,
      totalSec,
      startedAt:  Date.now(),
      isPaused:   false,
    });
  }, []);

  const pauseBlock = useCallback(() => {
    setLiveBlock(prev => {
      if (!prev || prev.isPaused) return prev;
      const elapsed = prev.startedAt ? Math.floor((Date.now() - prev.startedAt) / 1000) : (prev.totalSec - prev.secLeft);
      const secLeft = Math.max(0, prev.totalSec - elapsed);
      return { ...prev, isPaused: true, startedAt: null, secLeft };
    });
  }, []);

  const resumeBlock = useCallback(() => {
    setLiveBlock(prev => {
      if (!prev || !prev.isPaused) return prev;
      return { ...prev, isPaused: false, startedAt: Date.now() - (prev.totalSec - prev.secLeft) * 1000 };
    });
  }, []);

  /* Ending early still counts — log however long was actually focused. */
  const endBlock = useCallback(() => {
    const lb = liveBlockRef.current;
    if (lb) {
      const elapsed = lb.isPaused || !lb.startedAt
        ? lb.totalSec - lb.secLeft
        : Math.floor((Date.now() - lb.startedAt) / 1000);
      logBlockSession(lb, Math.max(0, Math.min(lb.totalSec, elapsed)), true);
    }
    setLiveBlock(null);
  }, [logBlockSession]);

  const value = useMemo(() => ({
    timeBlocks, refetchTimeBlocks: fetchTimeBlocks,
    liveBlock, startBlock, pauseBlock, resumeBlock, endBlock,
  }), [timeBlocks, fetchTimeBlocks, liveBlock, startBlock, pauseBlock, resumeBlock, endBlock]);

  return (
    <ScheduleContext.Provider value={value}>
      {children}

      {/* ── Global completion celebration — renders on whatever page the user is on ── */}
      {completedBlock && typeof document !== 'undefined' && createPortal(
        <>
          <style>{`
            @keyframes fp-toast-fly {
              0%   { opacity:0; transform: translate(calc(50vw - 190px), calc(50vh - 130px)) scale(0.35); }
              22%  { opacity:1; transform: translate(-50%, -50%) scale(1.06); }
              30%  { opacity:1; transform: translate(-50%, -50%) scale(1); }
              74%  { opacity:1; transform: translate(-50%, -50%) scale(1); }
              100% { opacity:0; transform: translate(-50%, -50%) scale(0.88); }
            }
            @keyframes fp-toast-bar {
              from { width: 100%; }
              to   { width: 0%; }
            }
            .fp-toast-close:hover { background: rgba(255,255,255,0.12) !important; }
          `}</style>

          <div
            onClick={() => setCompletedBlock(null)}
            style={{ position: 'fixed', inset: 0, zIndex: 99990 }}
          />

          <div
            style={{
              position: 'fixed',
              top: '50%',
              left: '50%',
              zIndex: 99999,
              animation: 'fp-toast-fly 5s cubic-bezier(0.22,1,0.36,1) both',
              pointerEvents: 'auto',
            }}
          >
            <div
              style={{
                background: 'linear-gradient(145deg, #1c1c35 0%, #14142a 100%)',
                border: '1px solid rgba(99,102,241,0.4)',
                borderRadius: 20,
                boxShadow: '0 24px 64px rgba(0,0,0,0.7), 0 0 0 1px rgba(99,102,241,0.18)',
                width: 320,
                overflow: 'hidden',
                position: 'relative',
              }}
              onClick={e => e.stopPropagation()}
            >
              <div style={{
                height: 3,
                background: 'linear-gradient(90deg,#6366f1,#a855f7,#ec4899,#f59e0b,#10b981)',
              }} />

              <div style={{ padding: '20px 20px 16px' }}>
                <div style={{ fontSize: 36, textAlign: 'center', letterSpacing: 6, marginBottom: 12 }}>
                  🎉✅🏆
                </div>

                <div style={{ textAlign: 'center', fontSize: 18, fontWeight: 800, color: '#fff', marginBottom: 6, letterSpacing: '-0.4px' }}>
                  Block Complete!
                </div>

                <div style={{ textAlign: 'center', marginBottom: 4 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: typeConf(completedBlock.type).dot }}>
                    {typeConf(completedBlock.type).emoji} {completedBlock.title}
                  </span>
                </div>

                <div style={{ textAlign: 'center', marginBottom: 18 }}>
                  <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)' }}>
                    {completedBlock.durationMin > 0
                      ? `${completedBlock.durationMin} min of focused work 🔥`
                      : 'Great work! 🔥'}
                  </span>
                </div>

                <button
                  className="fp-toast-close"
                  onClick={() => setCompletedBlock(null)}
                  style={{
                    display: 'block', width: '100%', padding: '8px 0',
                    borderRadius: 10, fontSize: 12, fontWeight: 600,
                    border: '1px solid rgba(255,255,255,0.1)',
                    background: 'rgba(255,255,255,0.06)',
                    color: 'rgba(255,255,255,0.5)', cursor: 'pointer',
                    transition: 'background 0.15s',
                  }}
                >
                  Dismiss
                </button>
              </div>

              <div style={{ height: 3, background: 'rgba(255,255,255,0.06)' }}>
                <div style={{
                  height: '100%',
                  background: `linear-gradient(90deg, ${typeConf(completedBlock.type).dot}, #6366f1)`,
                  animation: 'fp-toast-bar 5s linear forwards',
                }} />
              </div>
            </div>
          </div>
        </>,
        document.body
      )}
    </ScheduleContext.Provider>
  );
};

export default ScheduleProvider;
